import { Region, RegionCode, getMapRegionName } from 'shared/src/types/region.type';

/**
 * Country-name resolution for /region.
 *
 * The page speaks two vocabularies at once: the flag tiles and the Region enum
 * say "USA", the 3D globe says "United States of America" because that is how
 * world-atlas spells it. Anything that turns a name into a country code has to
 * accept both.
 *
 * This lives outside region.tsx so it can be tested. It was a closure over a
 * lookup table keyed by Region names only, and feeding it a globe name returned
 * null — which `beginCountrySelection` reads as "no map for this country" and
 * quietly redirects to nationwide results instead of opening the state picker.
 * Nothing failed; the visitor just never got the choice.
 */

/**
 * Region enum value -> internal country code.
 *
 * Region and RegionCode are parallel enums (same keys), so joining them by key
 * is the only mapping that cannot drift.
 */
export const REGION_NAME_TO_CODE: Readonly<Record<string, string>> = (() => {
  const map: Record<string, string> = {};
  for (const key of Object.keys(Region) as (keyof typeof Region)[]) {
    if (key === 'All') continue;
    const code = RegionCode[key as keyof typeof RegionCode];
    if (code) map[Region[key]] = code;
  }
  return map;
})();

/**
 * Canonical Region enum name for any spelling — enum value, world-atlas name,
 * or a known alias. Returns the input unchanged when nothing matches, so
 * callers keep whatever diagnostic value the original string had.
 */
export function toRegionName(name: string): string {
  return getMapRegionName(name) ?? name;
}

/**
 * Internal country code ("US", "UK", never "GB") for any spelling of a country.
 *
 * ⚠ Always normalize before the table lookup. Every country except the USA and
 * the UK has a Region value identical to its atlas spelling, so skipping this
 * appears to work for 22 of 24 countries and fails silently for the two biggest.
 */
export function countryCodeForAnyName(name: string): string | null {
  return REGION_NAME_TO_CODE[toRegionName(name)] ?? null;
}
